import { useState, useEffect } from 'react';
import { InsuranceBill, InsuranceBillResponse, ClaimsResponse, InsuranceBillClaim } from '../pages/hospital/Billing/types';
import { getInsuranceBillByUuidService, getClaimsForInsuranceBillService } from '../services/billing';

interface UseInsuranceBillProps {
    investigationUuid: string;
    billUuid: string;
    enabled?: boolean;
}

interface UseInsuranceBillResult {
    bill: InsuranceBill | null;
    claims: InsuranceBillClaim[];
    loading: boolean;
    error: string | null;
    refetch: () => void;
}

export function useInsuranceBill({
    investigationUuid,
    billUuid,
    enabled = true
}: UseInsuranceBillProps): UseInsuranceBillResult {
    const [bill, setBill] = useState<InsuranceBill | null>(null);
    const [claims, setClaims] = useState<InsuranceBillClaim[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchBill = async () => {
        if (!enabled || !investigationUuid || !billUuid) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);
            
            // Fetch bill and claims in parallel
            const [billResponse, claimsResponse]: [InsuranceBillResponse, ClaimsResponse] = await Promise.all([
                getInsuranceBillByUuidService(investigationUuid, billUuid),
                getClaimsForInsuranceBillService(investigationUuid, billUuid)
            ]);
            
            if (billResponse.status === 200 && billResponse.bill) {
                setBill(billResponse.bill);
            } else {
                setError(billResponse.errorMessage || 'Failed to fetch insurance bill');
                setBill(null);
            }

            if (claimsResponse.status === 200) {
                setClaims(claimsResponse.insuranceClaims || []);
            } else {
                setClaims([]);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch insurance bill');
            setBill(null);
            setClaims([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBill();
    }, [investigationUuid, billUuid, enabled]);

    return {
        bill,
        claims,
        loading,
        error,
        refetch: fetchBill
    };
}

